import React, { useState, useEffect, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { makeStyles } from '@mui/styles'
import { user } from 'ampersand-app'
import request from '@/modules/base/util/request'
import lang from 'lang'
import Decimal from 'decimal.js'
import { isEqual, isNumber, pick } from 'lodash'

import settingsConfig from '@/modules/settings/config'
import articleConfig from '@/modules/article/config'
import SettingsRadioInput from '@/modules/base/components/settings-radio-input/settings-radio-input'
import { taxTypes, USER_RIGHTS } from '@/modules/base/util/constants'
import { SelectInput } from '@/modules/base/components/inputs'
import { BookingService } from '@/modules/banking/services/bookingService'
import { accountsToOptions, findAccountOption } from './revenue-account-options'

import { fetchCompanyData, setCompanyData } from '@/redux/ducks/settings/company'

import { toast } from '@/modules/base/components/toast/toastConfig'

import useCheckAccessRights from '@/modules/_helpers/accessCheck/useCheckAccessRights'
import useClientSetting from '@/modules/client/_helpers/useClientSetting'
import { useNavigate } from 'react-router'
import { useOpenModal } from '@/modules/_helpers/openModal'

const useStyles = makeStyles((theme) => ({
  title: {
    fontWeight: theme.fontWeights.medium,
    fontSize: theme.fontSizes.smedium,
    marginBottom: 30,
  },
  dropdownWrapper: {
    marginTop: 20,
    maxWidth: '500px',
    paddingLeft: 40, // Align with radio button label content (radio button width ~40px)
  },
  select: {
    '& .react-select__control': {
      background: theme.backgroundColorLight,
    },
  },
  descriptionComponent: {
    marginTop: 10,
  },
}),
)

const {
  IS_SUBJECT_TO_IMPUTED_TAX_TRUE,
  IS_SUBJECT_TO_IMPUTED_TAX_FALSE,
  SMALL_BUSINESS,
  VAT_EXEMPT,
} = taxTypes

const GeneralTaxSettings = (props) => {
  const navigate = useNavigate()
  const openModal = useOpenModal()
  const dispatch = useDispatch()
  const { onSave = () => {}, refresh = false, isOnboardingTester = false } = props
  const classes = useStyles()
  const [selectedOption, setSelectedOption] = useState()
  const [revenueAccounts, setRevenueAccounts] = useState([])
  const [selectedRevenueAccount, setSelectedRevenueAccount] = useState(null)
  const [companyDataInitialized, setCompanyDataInitialized] = useState(false)
  const [defaultRevenueAccountNumber, setDefaultRevenueAccountNumber] = useState(null)
  const [isLoadingRevenueAccounts, setIsLoadingRevenueAccounts] = useState(false)
  const isSavingRef = useRef(false)
  const companyData = useSelector(store => store.settings.company)
  const { isClientSetting, client } = useClientSetting()

  const options = [
    {
      value: IS_SUBJECT_TO_IMPUTED_TAX_FALSE,
      title: lang.generalTaxCategoryTitleOption2,
      description: lang.generalTaxCategoryDescriptionOption2,
    },
    {
      value: IS_SUBJECT_TO_IMPUTED_TAX_TRUE,
      title: lang.generalTaxCategoryTitleOption1,
      description: lang.generalTaxCategoryDescriptionOption1,
    },
    {
      value: SMALL_BUSINESS,
      title: lang.generalTaxCategoryTitleOption3,
      description: lang.generalTaxCategoryDescriptionOption3,
    },
    {
      value: VAT_EXEMPT,
      title: lang.generalTaxCategoryTitleOption4,
      descriptionComponent: <div className={classes.descriptionComponent} dangerouslySetInnerHTML={{ __html: lang.generalTaxCategoryDescriptionOption4 }} />,
    },
  ]

  const readOnly = !useCheckAccessRights(USER_RIGHTS.SETTINGS.WRITE)

  const fetchRevenueAccounts = () => {
    setIsLoadingRevenueAccounts(true)
    return BookingService.chartOfAccounts.getInvoiceList({ smallbusiness: false }, false)
      .then((response) => {
        const { data: { vatGroups } } = response
        const vat0Group = vatGroups.find(group => group.key === 'vat0')
        if (vat0Group?.accounts) {
          setRevenueAccounts(vat0Group.accounts)
          setDefaultRevenueAccountNumber(vat0Group.default ?? null)
        }
      })
      .finally(() => {
        setIsLoadingRevenueAccounts(false)
      })
  }

  useEffect(() => {
    if (companyData?.isSmallBusiness === undefined || companyDataInitialized) return
    const { isSmallBusiness, isSubjectToImputedTaxation, defaultRevenueAccount } = companyData
    setCompanyDataInitialized(true)

    // If defaultRevenueAccount exists and is not null/undefined/empty, set to VAT_EXEMPT
    let taxSelectedTaxSetting
    if (defaultRevenueAccount !== null && defaultRevenueAccount !== '') {
      taxSelectedTaxSetting = VAT_EXEMPT
    } else {
      taxSelectedTaxSetting = isSmallBusiness
        ? SMALL_BUSINESS
        : isSubjectToImputedTaxation
          ? IS_SUBJECT_TO_IMPUTED_TAX_TRUE
          : IS_SUBJECT_TO_IMPUTED_TAX_FALSE
    }

    setSelectedOption(taxSelectedTaxSetting)
  }, [companyData])

  useEffect(() => {
    dispatch(fetchCompanyData(client?.id))
  }, [refresh])

  useEffect(() => {
    fetchRevenueAccounts()
  }, [])

  useEffect(() => {
    if (!revenueAccounts.length) return
    // the default can be missing or point at an account outside this list - then nothing is preselected
    setSelectedRevenueAccount(findAccountOption(revenueAccounts, defaultRevenueAccountNumber))
  }, [revenueAccounts, defaultRevenueAccountNumber])

  const revenueAccountOptions = useMemo(() => accountsToOptions(revenueAccounts), [revenueAccounts])

  const saveVatExemptAccount = (account) => {
    const { isSmallBusiness: isSmallBusinessOld } = companyData || {}
    const dataToSave = {
      isSmallBusiness: false,
      isSubjectToImputedTaxation: false,
      defaultRevenueAccount: `${account.value.accountNo}`,
    }
    if (!isEqual(pick(companyData, 'isSmallBusiness', 'isSubjectToImputedTaxation', 'defaultRevenueAccount'), dataToSave)) {
      handleSave(dataToSave).then((data) => handleAfterSave(data, isSmallBusinessOld, false)).then(onSave)
    }
  }

  const handleRevenueAccountChange = (account) => {
    setSelectedRevenueAccount(account)
    if (account?.value?.accountNo) saveVatExemptAccount(account)
  }

  const handleAfterSave = ({ recurringInvoiceCount, articleData }, isSmallBusinessOld, isSmallBusiness) => {
    if (articleData && Array.isArray(articleData.articleData) && articleData.articleData.length > 0) {
      openModal(close => ({
        component: 'ConfirmModal',
        props: {
          disableBackdropClick: true,
          title: lang.settings.taxes.graduatedPriceUpdateModal.title,
          description: lang.settings.taxes.graduatedPriceUpdateModal.description,
          confirmButtonLabel: lang.settings.taxes.graduatedPriceUpdateModal.confirmButtonLabel,
          abortButtonLabel: lang.settings.taxes.graduatedPriceUpdateModal.ignoreButtonLabel,
          onAbort: () => {
            checkRecurringInvoicesCount(isSmallBusinessOld, isSmallBusiness, recurringInvoiceCount)
            close()
          },
          onConfirm: () => {
            updateGraduatedPrice(articleData, isSmallBusiness)
            close()
            checkRecurringInvoicesCount(isSmallBusinessOld, isSmallBusiness, recurringInvoiceCount)
          },
        },
      }))
    }
  }

  const handleOptionChange = (value) => {
    setSelectedOption(value)

    const { isSmallBusiness: isSmallBusinessOld } = companyData || {}

    if (value === VAT_EXEMPT) {
      if (selectedRevenueAccount?.value?.accountNo) saveVatExemptAccount(selectedRevenueAccount)
    } else {
      const isSmallBusiness = value === SMALL_BUSINESS
      const dataToSave = {
        isSmallBusiness,
        isSubjectToImputedTaxation: value === IS_SUBJECT_TO_IMPUTED_TAX_TRUE,
        defaultRevenueAccount: null,
      }
      handleSave(dataToSave).then((data) => handleAfterSave(data, isSmallBusinessOld, isSmallBusiness)).then(onSave)
    }
  }

  const checkRecurringInvoicesCount = (isSmallBusinessOld, isSmallBusiness, recurringInvoiceCount) => {
    setTimeout(() => {
      if (isSmallBusinessOld && isSmallBusiness && recurringInvoiceCount) {
        openModal(close => ({
          component: 'ConfirmModal',
          props: {
            disableBackdropClick: true,
            title: lang.settings.taxes.recurringWarningModal.title,
            description: lang.settings.taxes.recurringWarningModal.description,
            confirmButtonLabel: lang.settings.taxes.recurringWarningModal.confirmButtonLabel,
            abortButtonLabel: lang.settings.taxes.recurringWarningModal.ignoreButtonLabel,
            onAbort: close,
            onConfirm: () => {
              navigate('/recurringinvoices')
              close()
            },
          },
        }))
      }
    }, 500)
  }

  const handleSave = (data, noToast = false) => {
    const headers = {}
    if (isClientSetting) headers['collaboration-id'] = client?.id

    return new Promise((resolve, reject) => {
      if (isSavingRef.current) {
        reject(new Error('Save operation already in progress'))
      }

      isSavingRef.current = true
      request(settingsConfig.account, {
        method: 'POST',
        auth: true,
        data,
        headers,
      })
        .then(response => {
          const { body: { data: { isSmallBusiness, useNetAsDefault, beginningOfFiscalYear, recurringInvoiceCount, articleData, isSubjectToImputedTaxation, defaultRevenueAccount } } } = response
          if (!isOnboardingTester && !noToast) {
            // also inform about deactivated e invoice generation
            if (isSmallBusiness) {
              const message = `<p>${lang.accountTaxSuccessMessage.default}</p>
              <h6>${lang.accountTaxSuccessMessage.smallBusiness.title}</h5>
              <p>${lang.accountTaxSuccessMessage.smallBusiness.message}</p>
              `
              toast.showToast({ message })
            } else {
              toast.showToast({ message: lang.accountTaxSuccessMessage.default })
            }
          }
          user.setTenantInfo({ isSmallBusiness, recurringInvoiceCount })
          dispatch(setCompanyData({ isSmallBusiness, useNetAsDefault, beginningOfFiscalYear, isSubjectToImputedTaxation, defaultRevenueAccount }))
          resolve({ isSmallBusiness, recurringInvoiceCount, articleData })
        })
        .catch((error) => {
          toast.showToast({ type: 'error', message: lang.accountTaxErrorMessage })
          reject(error)
        })
        .finally(() => {
          isSavingRef.current = false
        })
    })
  }

  const updateGraduatedPrice = (data, isSmallBusiness) => {
    const promises = data.articleData.map((article) => {
      if (isSmallBusiness) {
        // Set the grossUnitPrice property to 0 for all elements in the graduatedPriceList array for smallBusiness
        article.graduatedPriceList = article.graduatedPriceList.map((priceListItem) => ({
          ...priceListItem,
          grossUnitPrice: 0,
        }))
      }
      if (!isSmallBusiness) {
        article.graduatedPriceList = article.graduatedPriceList.map((priceListItem) => {
          const netUnitPrice = priceListItem.netUnitPrice
          const vatPercent = article.vatPercent
          const priceNet = parseFloat(netUnitPrice)
          const calculatedVat = 1 + (vatPercent / 100)
          const net = new Decimal(isNumber(priceNet) ? priceNet : priceNet.replace(',', '.') || 0)
          const gross = net.times(calculatedVat)
          const grossUnitPrice = gross.toDP(2).toNumber()
          return {
            ...priceListItem,
            grossUnitPrice,
          }
        })
      }

      return request(`${articleConfig.resourceUrl}/${article.id}`, {
        auth: true,
        method: 'PUT',
        data: article,
      })
    })
    Promise.all(promises).catch(() =>
      toast.showToast({ type: 'error', message: lang.articleSaveDefaultErrorMessage }),
    )
  }

  const isVatExemptSelected = selectedOption === VAT_EXEMPT

  const optionEls = options.map((option, index) => {
    const { value } = option
    const checked = selectedOption === value
    return (
      <SettingsRadioInput key={index} {...option} checked={checked} onChange={handleOptionChange} readOnly={readOnly} />
    )
  })

  return (
    <div className={classes.wrapper}>
      <div className={classes.title}>{lang.generalTaxCategoryTitle}</div>
      <div>
        {optionEls}
      </div>
      <div className={classes.dropdownWrapper}>
        <SelectInput
          handleChange={handleRevenueAccountChange}
          label={lang.generalTaxCategoryVatExemptReasonLabel}
          containerClass={classes.select}
          notAsync
          allowCreate={false}
          value={selectedRevenueAccount}
          options={revenueAccountOptions}
          readOnly={!isVatExemptSelected}
          customSelectOptions={{
            isLoading: isVatExemptSelected && isLoadingRevenueAccounts,
          }}
        />
      </div>
    </div>
  )
}

GeneralTaxSettings.propTypes = {
  onSave: PropTypes.func,
  refresh: PropTypes.bool,
  isOnboardingTester: PropTypes.bool,
}

export default GeneralTaxSettings
